import { AppDocumentHeadOverrides } from '@ms/yammer-app-document-meta';
import { DeferredGlobalCopilotDrawer } from '@ms/yammer-copilot/dist/components/GlobalCopilot';
import { useDelegationStore } from '@ms/yammer-features-insights';
import { useCanCurrentUserAccessAdminSettings } from '@ms/yammer-hooks/dist/adminSettings';
import { useLoadFeatureAccessInsights } from '@ms/yammer-hooks/dist/featureAccessInsights';
import { useLoadMultiTenantOrganization } from '@ms/yammer-hooks/dist/multiTenantOrganization';
import { usePushSubscription } from '@ms/yammer-hooks/dist/pushSubscriptions';
import { useServiceWorkerMessages } from '@ms/yammer-hooks/dist/pwa';
import { useLoadStorylineSettings } from '@ms/yammer-hooks/dist/storylineSettings';
import { useComplianceChecks } from '@ms/yammer-hooks/dist/survey';
import { useLoadUserInformationBarrierSettings } from '@ms/yammer-hooks/dist/userInformationBarrierSettings';
import { useCurrentLocale } from '@ms/yammer-i18n-react';
import { usePersistLocale } from '@ms/yammer-i18n-react/dist/hooks';
import { AppContextProvider } from '@ms/yammer-libs-app-context';
import { useGetActiveNetworkBrandTheme } from '@ms/yammer-network-brand-theme/dist/hooks';
import { LoadNetworkBrandTheme } from '@ms/yammer-network-brand-theme/dist/integration';
import { ReduxAnnouncerBridge } from '@ms/yammer-web-components/dist/accessibility/Announcer';
import ArticleScrollProvider from '@ms/yammer-web-components/dist/articles/ArticleScrollContext';
import MediaLightbox from '@ms/yammer-web-components/dist/messageAttachments/MediaLightbox';
import { LoadableRealtimeClient } from '@ms/yammer-web-components/dist/realtime/RealtimeClient';
import SessionContextProviderWithHooks from '@ms/yammer-web-components/dist/session/SessionContextProviderWithHooks';
import { SendPresenceState } from '@ms/yammer-web-components/dist/userPresence';
import AppContainer from '@ms/yammer-web-core-ui/dist/components/AppContainer';
import KeyboardHeadingNavigation from '@ms/yammer-web-core-ui/dist/components/KeyboardHeadingNavigation';
import { LoadableToaster } from '@ms/yammer-web-core-ui/dist/fluent9/components/Toast';
import { usePrepareIcons } from '@ms/yammer-web-core-ui/dist/util/icons';
import { ContainerWidthProvider } from '@ms/yammer-web-core-ui/dist/util/responsiveness';
import { useMainFeatureSettings } from '@ms/yammer-web-ui-features/dist/featureSettings';
import { UIFeaturesProvider } from '@ms/yammer-web-ui-features/dist/features';
import type { FC } from 'react';
import React from 'react';

import AppLayout from '../AppLayout';
import Dialogs from '../Dialogs';
import Routes from '../Routes';
import Skiplink from '../Skiplink';
import SuiteHeaderStatusProvider from '../SuiteHeader/SuiteHeaderStatusContext';

import DeferredAppFeatures from './DeferredAppFeatures';
import { useThemeWithSuiteHeaderOverrides } from './hooks';

const App: FC = () => {
  const complianceChecks = useComplianceChecks();
  const isFeedbackEnabled = complianceChecks.policyAllowFeedback !== 'Disabled';
  const { selectedOnBehalfOfUserId } = useDelegationStore();

  usePersistLocale();
  useServiceWorkerMessages();
  usePushSubscription();
  usePrepareIcons();
  useLoadStorylineSettings();
  useLoadMultiTenantOrganization();
  useLoadFeatureAccessInsights({ selectedOnBehalfOfUserId });
  useLoadUserInformationBarrierSettings();

  const features = useMainFeatureSettings({
    overrides: {
      navigation: {
        admin: useCanCurrentUserAccessAdminSettings(),
      },
    },
  });

  const locale = useCurrentLocale();
  const theme = useThemeWithSuiteHeaderOverrides();
  const { currentActiveTheme, currentActiveThemeCoreUITokenOverrides, currentActiveThemeV9, isCustomThemingEnabled } =
    useGetActiveNetworkBrandTheme(theme);

  const loadNetworkBrandTheme = isCustomThemingEnabled ? <LoadNetworkBrandTheme /> : null;

  return (
    <AppContainer
      theme={currentActiveTheme}
      coreuiTokenOverrides={currentActiveThemeCoreUITokenOverrides}
      fluent9Theme={currentActiveThemeV9}
      locale={locale}
    >
      <KeyboardHeadingNavigation />
      <UIFeaturesProvider features={features}>
        <SessionContextProviderWithHooks>
          <AppContextProvider mainColumnAriaRole="main">
            <ArticleScrollProvider>
              <AppDocumentHeadOverrides />
              <Skiplink />
              <SuiteHeaderStatusProvider>
                <ContainerWidthProvider>
                  <AppLayout>
                    <Routes />
                  </AppLayout>
                </ContainerWidthProvider>
              </SuiteHeaderStatusProvider>
              <Dialogs />
              <MediaLightbox />
              <LoadableRealtimeClient />
              <ReduxAnnouncerBridge />
              <SendPresenceState />
              <DeferredGlobalCopilotDrawer isFeedbackEnabled={isFeedbackEnabled} />
              <LoadableToaster />
              <DeferredAppFeatures />
            </ArticleScrollProvider>
          </AppContextProvider>
        </SessionContextProviderWithHooks>
      </UIFeaturesProvider>
      {loadNetworkBrandTheme}
    </AppContainer>
  );
};
export default App;
